/* ── 213B §12 / §13 — THE TAB OPENS AT THE CLICK ──
 *
 * Laurent, 30/09, from prod: « ticking "generate pdf" generates it, but does not
 * offer the "show pro forma" button » · « "sending to client" does not do
 * anything, should open an outlook message with PDF attached ». The window
 * opened its tab AFTER the server's answer (`window.open` after an `await`): a
 * window opened after an await has lost the click's permission, and a blocked
 * `window.open` returns null without throwing — the tree says so itself
 * (src/lib/emailArchiveClient.ts `runEmailReply`). The tree's way: open
 * `about:blank` inside the click, send the tab to its address when the answer
 * comes, close it on any failure. These helpers hold that, typed, outside the
 * window (ProFormaModal.tsx's count may not rise). */

/** A tab opened inside the click's own activation; null where the browser refused even that. */
export function openTabNow(): Window | null {
  // `globalThis`: eslint.config.mjs declares no browser globals for src/** (emailPdfLink.ts says why)
  try { return globalThis.open("about:blank", "_blank"); } catch { return null; }
}

/** The tab sent to `url` when the answer lands — or, with no url, closed. A tab the browser did not give is replaced
 *  by a plain open (which a blocker may still eat: the toast says what happened either way). */
export function landTab(w: Window | null, url: string | null): void {
  if (url) {
    if (w && !w.closed) w.location = url;
    else globalThis.open(url, "_blank", "noopener");
  } else if (w && !w.closed) {
    try { w.close(); } catch { /* already gone */ }
  }
}

/** A base64 PDF as a blob address the tab can show; null for a corrupt base64 (said, never swallowed — 163 §B.5). */
export function pdfBlobUrl(b64: string): string | null {
  try {
    const bytes = Uint8Array.from(globalThis.atob(b64), (c) => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  } catch (e) {
    globalThis.console.warn("[proforma] PDF blob failed:", e);
    return null;
  }
}

/** What the send answered about the Outlook draft (`sendResult`: `draftCreated`, `draftError`, `draftWebLink` —
 *  server/src/routes/invoices.js), read through `unknown`. */
export function draftOutcomeOf(sendResult: unknown): { created: boolean; error: string; link: string | null } {
  const sr = sendResult as { draftCreated?: unknown; draftError?: unknown; draftWebLink?: unknown } | null | undefined;
  const link = typeof sr?.draftWebLink === "string" && sr.draftWebLink ? sr.draftWebLink : null;
  const error = typeof sr?.draftError === "string" ? sr.draftError : ""; // Graph's sentence, or nothing
  return { created: sr?.draftCreated !== false, error, link };
}
